import React, { useState } from 'react';
import { ExternalLink, Calendar, Tag, ArrowUpRight } from 'lucide-react';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'web' | 'mobile' | 'custom' | 'ai'>('all');

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'web', name: 'Web Applications' },
    { id: 'mobile', name: 'Mobile Apps' },
    { id: 'custom', name: 'Custom Software' },
    { id: 'ai', name: 'AI/ML & Analytics' }
  ];

  const projects = [
    {
      title: 'Sitecore Enterprise Portal',
      category: 'web',
      categoryLabel: 'CMS & Web Portal',
      description: 'Headless Sitecore and Umbraco CMS architecture built for a leading multinational to deliver automated content distribution.',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['Sitecore CMS', 'Umbraco CMS', '.NET Core', 'React'],
      date: '2024'
    },
    {
      title: '.NET Enterprise ERP Systems',
      category: 'custom',
      categoryLabel: 'Custom Software',
      description: 'Distributed cloud ERP suite managing manufacturing workflows and asset tracking constructed using secure C# and ASP.NET API endpoints.',
      image: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['.NET Core', 'SQL Server', 'Angular', 'Docker'],
      date: '2024'
    },
    {
      title: 'Java Microservice Engine',
      category: 'custom',
      categoryLabel: 'Custom Software',
      description: 'Highly performant transactional database routing engine for billing verification, capable of processing concurrent tasks.',
      image: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'Kubernetes'],
      date: '2024'
    },
    {
      title: 'Staff Augmentation Platform',
      category: 'web',
      categoryLabel: 'Recruitment Services',
      description: 'Online candidate database and automated staffing pipeline portal built to coordinate vetting and hiring workflows.',
      image: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Tailwind'],
      date: '2023'
    },
    {
      title: 'Budget PHP Storefront',
      category: 'web',
      categoryLabel: 'Web Applications',
      description: 'Rapid, cost-effective e-commerce portal utilizing clean PHP MVC structures for a startup with constrained development budget limits.',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['PHP', 'MySQL', 'Laravel', 'Bootstrap'],
      date: '2023'
    },
    {
      title: 'Financial Analytics Dashboard',
      category: 'ai',
      categoryLabel: 'AI/ML & Analytics',
      description: 'Real-time financial dashboard with predictive analytics and automated reporting capabilities.',
      image: 'https://images.pexels.com/photos/590020/pexels-photo-590020.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['Python', 'React', 'TensorFlow', 'PostgreSQL'],
      date: '2023'
    },
    {
      title: 'Mobile Banking App',
      category: 'mobile',
      categoryLabel: 'Mobile Apps',
      description: 'Secure mobile banking application with biometric authentication and real-time transaction monitoring.',
      image: 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=500',
      technologies: ['React Native', 'Node.js', 'Firebase', 'Blockchain'],
      date: '2023'
    }
  ];

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Proven Excellence</span>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-secondary">
            Our Successful <span className="text-brand-primary">Portfolio</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Explore our project case-studies showcasing secure software builds and seamless cloud migrations.
          </p>
        </div>

        {/* Dynamic Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id as any)}
              className={`px-5 py-2.5 rounded-full border text-sm font-semibold transition-all duration-200 ${
                activeCategory === cat.id 
                  ? 'bg-brand-primary text-white border-brand-primary shadow-md scale-105' 
                  : 'bg-gray-50 text-gray-700 border-gray-250 hover:border-brand-primary hover:bg-gray-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 group border border-gray-150 hover:border-brand-primary flex flex-col justify-between h-[450px] animate-scale-in"
            >
              <div>
                <div className="relative overflow-hidden h-48 bg-gray-100">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-350"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-brand-primary text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      {project.categoryLabel}
                    </span>
                  </div>
                </div>
                
                <div className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-brand-secondary">
                      {project.title}
                    </h3>
                    <div className="flex items-center text-gray-400 text-xs">
                      <Calendar className="h-3.5 w-3.5 mr-1" />
                      <span>{project.date}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-650 text-xs leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
              
              <div className="p-6 pt-0 space-y-4">
                <div className="flex flex-wrap gap-1.5 border-t border-gray-100 pt-4">
                  {project.technologies.map((tech, techIndex) => (
                    <span 
                      key={techIndex}
                      className="bg-brand-light text-brand-dark px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                
                <button 
                  onClick={() => { window.location.hash = '#contact'; }}
                  className="flex items-center text-xs font-bold text-brand-primary hover:text-brand-dark group-hover:translate-x-1.5 transition-transform duration-200"
                >
                  View Case Study
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;